import { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const empty = { name:'', email:'', password:'', role:'staff' };

export default function Users() {
  const [users, setUsers]     = useState([]);
  const [loading, setLoad]    = useState(true);
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);

  // We re-use the /auth/me approach; staff users are stored with role='staff'
  // For simplicity we fetch by calling profile update; in a full app add a /users endpoint
  // Here we demonstrate the UI — backend already supports role in users table
  const fetch = async () => {
    setLoad(true);
    // NOTE: A full multi-user system would have GET /users endpoint (admin only).
    // For MVP we show only current admin info and allow creating staff users.
    try {
      const res = await api.get('/auth/me');
      setUsers([res.data.user]);
    } catch {}
    setLoad(false);
  };

  useEffect(() => { fetch(); }, []);

  const openNew  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name:u.name, email:u.email, password:'', role:u.role }); setModal(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (!editing) {
        // Create new staff user — requires a POST /users endpoint (admin only)
        // For now, show info that it can be added via DB
        toast('Staff user creation requires direct DB access in MVP. Use phpMyAdmin to add rows to the users table with role=staff.', { icon:'ℹ️', duration:6000 });
      } else {
        await api.put('/auth/profile', { name:form.name, role:form.role, ...(form.password&&{password:form.password}) });
        toast.success('User updated!'); await fetch();
      }
      setModal(false);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div><h2 className="page-title">User Management</h2><p className="page-subtitle">Manage admin and staff access</p></div>
        <button className="btn btn-primary" onClick={openNew}>+ Add User</button>
      </div>

      <div className="alert alert-ok" style={{ marginBottom:16 }}>
        🛡️ <strong>Role-Based Access:</strong> Admins have full access. Staff can view all records and toggle paid/unpaid status but cannot edit or delete invoices, or access admin settings.
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--text3)' }}>Loading…</div>
        : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map(u=>(
                  <tr key={u.id}>
                    <td>
                      <div style={{ display:'flex',alignItems:'center',gap:9 }}>
                        <div style={{ width:30,height:30,background:'linear-gradient(135deg,var(--accent),var(--accent2))',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:700,fontSize:12,flexShrink:0 }}>
                          {u.name?.[0]?.toUpperCase()}
                        </div>
                        <span style={{ fontWeight:600 }}>{u.name}</span>
                      </div>
                    </td>
                    <td>{u.email}</td>
                    <td><span className={`badge badge-${u.role}`}>{u.role}</span></td>
                    <td><button className="btn btn-sm btn-secondary btn-icon" onClick={()=>openEdit(u)}>✏️</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop:18 }}>
        <div className="card-header"><span className="card-title">🔒 Permissions Matrix</span></div>
        <div className="table-wrapper">
          <table>
            <thead><tr><th>Feature</th><th>Admin</th><th>Staff</th></tr></thead>
            <tbody>
              {[
                ['View Dashboard',         '✅','✅'],
                ['Manage Clients',         '✅','✅'],
                ['Create Quotations',      '✅','✅'],
                ['Edit / Delete Quotations','✅','✅'],
                ['Create Invoices',        '✅','❌'],
                ['Edit Invoices',          '✅','❌'],
                ['Delete Invoices',        '✅','❌'],
                ['Toggle Paid / Unpaid',   '✅','✅'],
                ['Manage Renewals',        '✅','✅'],
                ['Delete Renewals',        '✅','❌'],
                ['User Management',        '✅','❌'],
                ['Settings & Branding',    '✅','❌'],
              ].map(([f,a,s])=>(
                <tr key={f}>
                  <td>{f}</td>
                  <td style={{ fontSize:16 }}>{a}</td>
                  <td style={{ fontSize:16 }}>{s}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal modal-sm">
            <div className="modal-header">
              <h3 style={{ fontFamily:'Syne,sans-serif',fontWeight:700 }}>{editing?'Edit User':'New User'}</h3>
              <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>setModal(false)}>✕</button>
            </div>
            <form onSubmit={save}>
              <div className="modal-body">
                <div className="form-group"><label className="form-label">Name *</label>
                  <input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} required /></div>
                <div className="form-group"><label className="form-label">Email *</label>
                  <input type="email" className="form-control" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} disabled={!!editing} required /></div>
                <div className="form-group"><label className="form-label">{editing?'New Password (leave blank to keep)':'Password *'}</label>
                  <input type="password" className="form-control" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} required={!editing} /></div>
                <div className="form-group"><label className="form-label">Role</label>
                  <select className="form-control" value={form.role} onChange={e=>setForm({...form,role:e.target.value})}>
                    <option value="admin">Admin</option><option value="staff">Staff</option>
                  </select></div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?<span className="spinner"/>:(editing?'Update':'Create')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
